import { useNavigate } from 'react-router-dom'
import { LockKeyhole } from 'lucide-react'
import { useLanguage } from '../../i18n/index.jsx'
import useAuthStore from '../../store/authStore.js'
import { getModuleGateMeta } from '../../config/featureModules.js'

export interface UpgradeBannerProps {
  planName?: string
  module?: string
  /** BILL-5: 'past_due' = access lost to a failed payment, not the plan tier.
      Default 'plan' keeps the historical upgrade copy. */
  reason?: 'plan' | 'past_due'
}

export default function UpgradeBanner({ planName, module, reason = 'plan' }: UpgradeBannerProps) {
  const navigate = useNavigate()
  const { t } = useLanguage()
  const isAdmin = useAuthStore(s => s.isAdmin?.() ?? false)
  const meta = getModuleGateMeta(module)
  const featureName = meta.label ? t(meta.label) : (module || t('upgradeFallbackFeature'))
  const featureDescription = meta.description ? t(meta.description) : ''
  const requiredPlan = meta.requiredPlan || 'Pro+'

  return (
    <div style={{
      display: 'flex', flexDirection: 'column', alignItems: 'center',
      justifyContent: 'center', minHeight: '60vh', padding: 40, textAlign: 'center',
    }}>
      <div style={{
        background: 'var(--bg-surface)', border: '1px solid var(--border-default)',
        borderRadius: 16, padding: '40px 32px', maxWidth: 480, width: '100%',
      }}>
        <div style={{
          fontSize: 'var(--text-display)', marginBottom: 16,
          background: 'var(--accent-dim)', borderRadius: '50%',
          width: 80, height: 80, display: 'flex', alignItems: 'center',
          justifyContent: 'center', margin: '0 auto 16px',
          color: 'var(--accent)',
        }}>
          <LockKeyhole size={34} aria-hidden="true" />
        </div>
        <h2 style={{ fontSize: 'var(--text-title)', fontWeight: 700, color: 'var(--text-primary)', marginBottom: 8 }}>
          {t('upgradeRequiredNamed', { featureName })}
        </h2>
        {featureDescription && (
          <p style={{ fontSize: 'var(--text-base)', color: 'var(--text-secondary)', lineHeight: 1.5, marginBottom: 12 }}>
            {featureDescription}
          </p>
        )}
        <p style={{ fontSize: 'var(--text-base)', color: 'var(--text-muted)', lineHeight: 1.6, marginBottom: 24 }}>
          {reason === 'past_due'
            ? (isAdmin ? t('featurePastDueAdmin', { featureName }) : t('featurePastDueMember', { featureName }))
            : (isAdmin
              ? (t('upgradeDescriptionNamed', { featureName, requiredPlan }))
              : (t('upgradeContactAdminNamed', { featureName, requiredPlan })))}
        </p>
        <div style={{
          fontSize: 'var(--text-sm)', color: 'var(--text-muted)', marginBottom: 20,
          padding: '6px 12px', background: 'var(--bg-elevated)', borderRadius: 8,
          display: 'inline-block',
        }}>
          {t('currentPlan')}: <strong style={{ textTransform: 'capitalize' }}>{planName}</strong>
        </div>
        <div style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
          <button
            onClick={() => navigate('/')}
            style={{
              padding: '10px 20px', borderRadius: 10,
              background: 'var(--bg-elevated)', color: 'var(--text-secondary)',
              border: '1px solid var(--border-default)', fontSize: 'var(--text-base)', cursor: 'pointer',
            }}
          >
            {t('goBack')}
          </button>
          {isAdmin && (
            <button
              onClick={() => navigate('/settings/billing')}
              style={{
                padding: '10px 24px', borderRadius: 10,
                background: 'var(--accent)', color: 'var(--text-inverse)',
                border: 'none', fontSize: 'var(--text-base)', fontWeight: 600, cursor: 'pointer',
              }}
            >
              {reason === 'past_due' ? t('pastDueUpdatePayment') : t('upgradePlan')}
            </button>
          )}
        </div>
      </div>
    </div>
  )
}
